import { Popover, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { Fragment } from 'react'
import React, { useEffect, useRef, useState } from "react";
import DropMIC from '../icons/DropDown/DropMIC';
import TestMic from "../icons/DropDown/TestMic"
import TestMicOff from '../icons/DropDown/TestMicOff';
import PauseButton from '../icons/DropDown/PauseButton';
import { useMeetingAppContext } from '../MeetingAppContextDef';
import useIsMobile from '../hooks/useIsMobile';

export default function DropDown({
  mics,
  changeMic,
  customAudioStream,
  audioTrack,
  micOn,
  didDeviceChange,
  setDidDeviceChange,
  testSpeaker,
  setTestSpeaker
}) {

  const {
    setSelectedMic,
    selectedMic,
    selectedSpeaker,
    isMicrophonePermissionAllowed
  } = useMeetingAppContext();
  const [audioProgress, setAudioProgress] = useState(0);
  const [recordingProgress, setRecordingProgress] = useState(0)
  const [recordingStatus, setRecordingStatus] = useState("inactive");
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [volume, setVolume] = useState(null);
  const [audio, setAudio] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useIsMobile()

  const testingSpeaker = () => {
    setTestSpeaker(!testSpeaker)
  }

  const audioTrackRef = useRef();
  const intervalRef = useRef();
  const audioAnalyserIntervalRef = useRef();
  const mediaRecorder = useRef(null);

  const mimeType = "audio/webm";

  useEffect(() => {
    audioTrackRef.current = audioTrack;

    if (audioTrack) {
      analyseAudio(audioTrack);
    } else {
      stopAudioAnalyse();
    }
  }, [audioTrack]);

  useEffect(() => {
    if (didDeviceChange) {
      setDidDeviceChange(false)
      if (mediaRecorder.current != null && mediaRecorder.current.state === "recording") { stopRecording() }
      setRecordingProgress(0)
      setRecordingStatus("inactive")
    }
  }, [didDeviceChange])

  const analyseAudio = (audioTrack) => {
    const audioStream = new MediaStream([audioTrack]);
    const audioContext = new AudioContext();
    const audioSource = audioContext.createMediaStreamSource(audioStream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = 512;
    analyser.minDecibels = -127;
    analyser.maxDecibels = 0;
    analyser.smoothingTimeConstant = 0.4;

    audioSource.connect(analyser);

    const volumes = new Uint8Array(analyser.frequencyBinCount);
    const volumeCallback = () => {
      analyser.getByteFrequencyData(volumes);
      const volumeSum = volumes.reduce((sum, vol) => sum + vol);
      const averageVolume = volumeSum / volumes.length;
      setVolume(averageVolume);
    };
    audioAnalyserIntervalRef.current = setInterval(volumeCallback, 100);
  };

  const stopAudioAnalyse = () => {
    clearInterval(audioAnalyserIntervalRef.current);
  };

  const handlePlaying = () => {
    setRecordingStatus("playing");
    const audioTags = document.getElementsByTagName("audio");

    for (let i = 0; i < audioTags.length; i++) {
      audioTags.item(i).setSinkId(selectedSpeaker.id).then(() => {
        audioTags.item(i).play();
        audioTags.item(i).addEventListener('timeupdate', () => {
          const progress = (audioTags.item(i).currentTime / recordingDuration) * 100;
          setAudioProgress(progress);
        });
        audioTags.item(i).addEventListener('ended', () => {
          setAudioProgress(0);
        });
      })
    }
  }

  const startRecording = async () => {
    setRecordingStatus("recording");

    try {
      const media = new MediaRecorder(customAudioStream, { type: mimeType });
      mediaRecorder.current = media;
      mediaRecorder.current.start();
      let localAudioChunks = [];

      mediaRecorder.current.ondataavailable = (event) => {
        if (typeof event.data === "undefined") return;
        if (event.data.size === 0) return;
        localAudioChunks.push(event.data);
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(localAudioChunks, { type: mimeType });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudio(audioUrl);
        localAudioChunks = []
        const elapsedTime = Date.now() - startTime;
        const durationInSeconds = (elapsedTime / 1000);
        setRecordingDuration(durationInSeconds)
      };

      const startTime = Date.now();
      intervalRef.current = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const progress = (elapsedTime / 7000) * 100;
        setRecordingProgress(progress);
      });

      setTimeout(() => {
        clearInterval(intervalRef.current)
        stopRecording();
      }, 7000)
    } catch (err) {
      console.log("Error in MediaRecorder:", err)
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current.state != "inactive") {
      setRecordingProgress(0);
      setRecordingStatus("stopped recording");
      clearInterval(intervalRef.current)
      mediaRecorder.current.stop();
    }
  };

  return (
    <>
      <Popover className="relative w-full z-20">
        {({ open }) => (
          <>
            <Popover.Button
              onMouseEnter={() => { setIsHovered(true) }}
              onMouseLeave={() => { setIsHovered(false) }}
              disabled={!isMicrophonePermissionAllowed}
              className={`focus:outline-none  
              text-violet-700
              group inline-flex items-center rounded-md px-1 py-1 w-full text-base font-normal
              ${!isMicrophonePermissionAllowed ? "opacity-50" : ""}`}
              onClick={() => {
                if (mediaRecorder.current != null && mediaRecorder.current.state == "recording") { stopRecording() }
                setRecordingProgress(0)
                setRecordingStatus("inactive")
              }}
            >
              <div>
                <DropMIC fillColor={"#7c3aed"} />

              </div>
              <span className="overflow-hidden whitespace-nowrap overflow-ellipsis w-full ml-6">
                {isMicrophonePermissionAllowed ? selectedMic?.label : "Permission Needed"}
              </span>
              <ChevronDownIcon
                className={`${open ? 'text-violet-600' : 'text-violet-400'}
                ml-8 h-5 w-10 transition duration-150 ease-in-out group-hover:text-violet-500 mt-1`}
                aria-hidden="true"
              />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute bottom-full z-50 mb-2 w-full px-4 sm:px-0">
                <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="bg-white border border-violet-200 rounded-lg">
                    <div>
                      <div className="flex flex-col">
                        {mics.map(
                          (item, index) => {
                            return (
                              item?.kind === "audioinput" && (
                                <div
                                  key={`mics_${index}`}
                                  className={` my-1 pl-4 pr-2 text-violet-700 text-left flex hover:bg-violet-50 rounded-md transition-colors duration-150`}
                                >
                                  <span className="w-6 mr-2 flex items-center justify-center">
                                    {selectedMic?.label === item?.label && (
                                      <CheckIcon className='h-5 w-5 text-violet-600' />
                                    )}
                                  </span>
                                  <button
                                    className={`flex flex-1 w-full text-left`}
                                    value={item?.deviceId}
                                    onClick={() => {
                                      setSelectedMic(
                                        (s) => ({
                                          ...s,
                                          label: item?.label,
                                          id: item?.deviceId,
                                        })
                                      );
                                      changeMic(item?.deviceId);
                                      if (mediaRecorder.current != null && mediaRecorder.current.state == "recording") { stopRecording() }
                                      setRecordingProgress(0)
                                      setRecordingStatus("inactive")
                                    }}
                                  >
                                    {item?.label ? (
                                      <span>{item?.label}</span>
                                    ) : (
                                      <span>{`Mic ${index + 1}`}</span>
                                    )}
                                  </button>
                                </div>
                              )
                            );
                          }
                        )}

                        <hr className='border border-violet-200 mt-2' />

                        {micOn ? <div className={`rounded-b-lg py-2 bg-violet-400 text-violet-700 flex ${isMobile ? "flex-col" : "flex-1"} w-full text-left pl-2`} >
                          <div className={`flex flex-row ${!isMobile && "w-2/3"} `}>
                            <span className="mr-4 mt-1">
                              <TestMic />
                            </span>

                            <div className={`${isMobile ? "w-full mr-5" : "w-36"} mt-3 bg-violet-100 rounded-full h-1`}>
                              <div className="bg-violet-600 h-1 rounded-full transition-all duration-150" style={{ width: `${volume / 256 * 100}%` }} ></div>
                            </div>
                          </div>

                          {!isMobile && <div className={`${!isMobile && "w-1/3"} w-full flex justify-center `}>
                            {recordingStatus == "inactive" && <button className='px-2 py-1 text-xs rounded bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors duration-150' onClick={startRecording}>
                              Record
                            </button>}


                            {recordingStatus == "stopped recording" && <button className='px-2 py-1 text-xs rounded bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors duration-150' onClick={handlePlaying}>
                              Play
                            </button>}

                            {recordingStatus == "recording" && <button className='px-2 py-1 mx-1 py-4 text-xs rounded w-full bg-violet-100 text-violet-700 relative z-0' onClick={stopRecording}>
                              <div className=' h-full rounded bg-violet-300 absolute top-0 left-0 ' style={{ width: `${recordingProgress}%` }} >
                                <PauseButton />
                              </div>
                            </button>}

                            {recordingStatus == "playing" && <button className='px-2 py-1 mx-1 py-4 text-xs rounded w-full bg-violet-100 text-violet-700 relative z-0' onClick={handlePlaying}>
                              <div className=' h-full rounded bg-violet-300 absolute top-0 left-0 ' style={{ width: `${audioProgress}%` }} >
                                <PauseButton />
                              </div>
                            </button>}
                          </div>}
                          {isMobile &&
                            <div className='flex flex-col mt-1 items-center justify-center'>
                              {testSpeaker && <p className='text-xs text-violet-500 text-center'>Speak to test mic & speakers...</p>}
                              <button className='p-2 text-xs mt-1 rounded w-fit bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors duration-150' onClick={testingSpeaker}>
                                {!testSpeaker ? "Test Mic and Speaker" : "Stop Test"}
                              </button></div>
                          }


                        </div>
                          :
                          <div className=' text-violet-400 bg-violet-400 flex flex-1 items-center w-full rounded-b-lg py-1 pl-5'>
                            <TestMicOff />
                            Unmute to test your mic
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
      <audio src={audio} ></audio>
    </>
  )
}

